import React, { useState, useRef, useEffect } from 'react';
import backIcon from '../images/back-icon.svg';
import editIcon from '../images/edit-icon.svg';
import DiaProfilePicture from '../images/dia-profile-picture.jpeg';
import './profile.css';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading/Loading';
import Toast from '../components/Toast/Toast';
import { apiCall } from '../utils/api';
import SaveIcon from '../images/save-icon.svg';
import trackEvent from '../utils/analytics.js';
import chaimateLogo from '../images/chaimate-logo.svg';
import threeDotsIcon from '../images/three-dots-icon.svg';
const ROLE_NEEDS = [
    { role: 'CA', need: "Completed CA, practising or not isn't important" },
    { role: 'CS', need: "Completed CS, practising or not isn't important" },
    { role: 'Aspiring CA', need: 'Students aspiring or in the process of becoming CA' },
    { role: 'Aspiring CS', need: 'Students aspiring or in the process of becoming CS' },
    { role: 'Organisation', need: 'Company or Tuition classes' }
];

const Profile = ({ profileData, setShowProfile, setProfileData, readOnly = false }) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [name, setName] = useState(profileData.name || 'Dia');
    const [bio, setBio] = useState(profileData.bio || 'Life is good when you have me');
    const [profileImage, setProfileImage] = useState(profileData.image_url || DiaProfilePicture);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const onboardingChoices = profileData?.onboarding_choices || profileData?.onboarding || {};
    const contactNumber =
        profileData?.phone ||
        profileData?.phone_number ||
        profileData?.mobile ||
        profileData?.number ||
        profileData?.contact_number ||
        '';
    const cityValue =
        onboardingChoices?.city ||
        profileData?.city ||
        profileData?.location ||
        '';
    const roleValue =
        onboardingChoices?.role ||
        profileData?.current_role ||
        profileData?.role ||
        profileData?.designation ||
        profileData?.job_title ||
        '';
    const qualificationValue =
        onboardingChoices?.qualification ||
        profileData?.qualification ||
        '';
    const normalizedRole = roleValue?.toLowerCase() || '';
    const normalizedQualification = qualificationValue?.toLowerCase() || '';
    const isCaOrCs = ['ca', 'chartered accountant', 'cs', 'company secretary'].some(keyword => (
        normalizedRole.includes(keyword) || normalizedQualification.includes(keyword)
    ));
    const caCsOrganisation =
        onboardingChoices?.ca_organisation ||
        onboardingChoices?.ca_organization ||
        profileData?.ca_organisation ||
        profileData?.ca_organization ||
        profileData?.organisation ||
        profileData?.organization ||
        profileData?.company ||
        profileData?.company_name ||
        profileData?.current_company ||
        '';
    const organisationValue =
        onboardingChoices?.organization ||
        onboardingChoices?.organisation ||
        onboardingChoices?.affiliation ||
        profileData?.organization ||
        profileData?.organisation ||
        profileData?.company ||
        profileData?.company_name ||
        profileData?.affiliation ||
        '';
    const stageValue =
        onboardingChoices?.stage ||
        profileData?.stage ||
        profileData?.aspiring_stage ||
        profileData?.progress_stage ||
        '';
    const experienceValue =
        onboardingChoices?.total_experience_years ??
        profileData?.total_experience_years ??
        profileData?.experience_years ??
        profileData?.experience ??
        null;
    const expertiseRaw =
        onboardingChoices?.expertise ||
        profileData?.expertise ||
        profileData?.skills ||
        profileData?.specialities ||
        '';
    const expertiseValue = Array.isArray(expertiseRaw)
        ? expertiseRaw.filter(Boolean).join(', ')
        : expertiseRaw;
    const resumeLink =
        onboardingChoices?.resume_url ||
        onboardingChoices?.resume ||
        profileData?.resume_url ||
        profileData?.resume ||
        profileData?.resume_link ||
        profileData?.resumeUrl ||
        '';
    const formatExperienceLabel = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
            if (numericValue === 0) {
                return 'Fresher';
            }
            return `${numericValue} years experience`;
        }
        return String(value);
    };
    const experienceLabel = formatExperienceLabel(experienceValue);
    const normalizedResumeUrl = resumeLink
        ? (resumeLink.startsWith('http') ? resumeLink : `https://${resumeLink}`)
        : '';
    const detailFields = [
        { label: 'Number', value: contactNumber },
        { label: 'City', value: cityValue },
        { label: 'Role', value: roleValue },
        ...(isCaOrCs && caCsOrganisation ? [{ label: 'CA/CS organisation', value: caCsOrganisation }] : []),
        ...(stageValue ? [{ label: 'Stage', value: stageValue }] : []),
        ...(organisationValue ? [{ label: 'Organisation', value: organisationValue }] : []),
        ...(experienceLabel ? [{ label: 'Experience', value: experienceLabel }] : []),
        ...(expertiseValue ? [{ label: 'Expertise', value: expertiseValue }] : []),
        ...(normalizedResumeUrl ? [{
            label: 'Resume',
            value: (
                <a
                    href={normalizedResumeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="resume-link"
                >
                    View resume
                </a>
            )
        }] : [])
    ].filter(field => field.value);

    const metricSources = [
        profileData,
        profileData?.stats,
        profileData?.metrics,
        onboardingChoices,
    ];

    const getMetricValue = (keys, fallback = 0) => {
        for (const key of keys) {
            for (const source of metricSources) {
                if (source && typeof source === 'object' && key in source) {
                    const numeric = Number(source[key]);
                    if (!Number.isNaN(numeric)) {
                        return numeric;
                    }
                }
            }
        }
        return fallback;
    };

    const clampPercent = (value) => {
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return 0;
        return Math.min(Math.max(numeric, 0), 100);
    };

    const overallSuccessPercent = getMetricValue(['overall_success_percent', 'overall_success', 'success_percent'], 0);
    const dailyAvgHours = getMetricValue(['daily_avg_hours', 'daily_avg_hrs', 'daily_average_hours'], 0);
    const syllabusDonePercent = getMetricValue(['syllabus_done_percent', 'syllabus_percent', 'syllabus_done'], 0);
    const longestStreakDays = getMetricValue(['longest_streak_days', 'longest_streak'], 0);
    const daysLeftToExam = getMetricValue(['days_left_to_exam', 'days_to_exam', 'days_remaining'], 0);
    const retentionRatio = getMetricValue(['retention_ratio', 'retention'], 0);

    const metrics = [
        {
            id: 'overallSuccess',
            label: 'Overall Success %',
            value: `${Math.round(overallSuccessPercent)}%`,
            progress: clampPercent(overallSuccessPercent),
        },
        {
            id: 'dailyAvg',
            label: 'Daily Avg. (7d)',
            value: `${dailyAvgHours.toFixed(1)} hrs`,
            progress: clampPercent((dailyAvgHours / 5) * 100),
        },
        {
            id: 'syllabusDone',
            label: 'Syllabus done',
            value: `${Math.round(clampPercent(syllabusDonePercent))}%`,
            progress: clampPercent(syllabusDonePercent),
        },
        {
            id: 'longestStreak',
            label: 'Longest streak (Year)',
            value: `${Math.round(longestStreakDays)} Days`,
            progress: clampPercent(Math.min((longestStreakDays / 30) * 100, 100)),
        },
        {
            id: 'daysLeft',
            label: 'Days left to exam',
            value: `${Math.round(daysLeftToExam)} Days`,
            progress: clampPercent(Math.max(0, 100 - (daysLeftToExam / 365) * 100)),
        },
        {
            id: 'retention',
            label: 'Retention ratio',
            value: `${Math.round(retentionRatio)}%`,
            progress: clampPercent(retentionRatio),
        },
    ];

    const handleSource =
        profileData?.handle ||
        profileData?.username ||
        profileData?.user_name ||
        profileData?.slug ||
        profileData?.social_media_handle?.instagram ||
        profileData?.social_media_handle?.twitter ||
        profileData?.tag ||
        name;
    const sanitizedHandle = handleSource
        ? String(handleSource).replace(/\s+/g, '').toLowerCase()
        : 'dia';
    const displayHandle = `@${sanitizedHandle}`;

    const baseRole = roleValue || profileData?.current_role || profileData?.role || 'Learner';
    const stageLabelText = stageValue || profileData?.stage || profileData?.aspiring_stage;
    const roleLine = [baseRole, stageLabelText].filter(Boolean).join(' • ');

    const getAccessToken = () => {
        return document.cookie
            .split('; ')
            .find(row => row.startsWith('access_token='))
            ?.split('=')[1];
    };

    const handleImageClick = () => {
        if (readOnly) return;
        fileInputRef.current?.click();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setErrorMessage('Please select a valid image file');
                return;
            }

            // Validate file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setErrorMessage('Image size should be less than 5MB');
                return;
            }

            setSelectedImageFile(file);

            // Create preview URL
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfileImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageSelect = async (file) => {
        if (file) {
            setIsLoading(true);
            try {
                // Request signed URL
                let response = await apiCall.get('/image/request/signed/url');
                if (!response.status) {
                    throw new Error('Failed to get signed URL');
                }

                const signedUrl = response.data;
                const imageUrl = URL.createObjectURL(file);

                // Upload to GCS immediately
                const uploadResponse = await fetch(signedUrl, {
                    method: 'PUT',
                    body: file,
                    headers: {
                        'Content-Type': file.type
                    }
                });

                if (!uploadResponse.ok) {
                    throw new Error('Failed to upload image');
                }

                return signedUrl.split('?')[0];
            } catch (error) {
                console.error('Error uploading image:', error);
            }
            setIsLoading(false);
        }
    };

    // Auto-save when image changes
    useEffect(() => {
        if (selectedImageFile) {
            const autoSaveImage = async () => {
                try {
                    setIsLoading(true);
                    let imageUrl = profileData.image_url;

                    // Upload new image
                    imageUrl = await handleImageSelect(selectedImageFile);

                    const data = await apiCall.post('/dia_profile/upsert', {
                        name: name,
                        image_url: imageUrl,
                        bio: bio
                    });

                    if (!data.status) {
                        if (data.error_code === 1006) {
                            setErrorMessage(data.error_message || 'Unauthorized');
                            navigate('/');
                            return;
                        }
                        setErrorMessage(data.error_message || 'Failed to save profile');
                    } else {
                        setProfileData(data.data);
                        setSelectedImageFile(null);
                        setErrorMessage('');
                    }
                } catch (error) {
                    setErrorMessage('Failed to save profile. Please try again.');
                } finally {
                    setIsLoading(false);
                }
            };

            autoSaveImage();
        }
    }, [selectedImageFile]);

    const saveProfile = async () => {
        const accessToken = getAccessToken();
        if (!accessToken) {
            navigate('/');
            return;
        }

        // Don't save if an image is currently being auto-saved
        if (selectedImageFile) {
            return;
        }

        setIsLoading(true);
        try {
            let imageUrl = profileData.image_url;

            // Upload new image if selected
            if (selectedImageFile) {
                imageUrl = await handleImageSelect(selectedImageFile);
            }

            const data = await apiCall.post('/dia_profile/upsert', {
                name: name,
                image_url: imageUrl,
                bio: bio
            });

            setIsLoading(false);

            if (!data.status) {
                if (data.error_code === 1006) {
                    setErrorMessage(data.error_message || 'Unauthorized');
                    navigate('/');
                    return;
                }
                setErrorMessage(data.error_message || 'Failed to save profile');
            } else {
                setProfileData(data.data);
                setIsEditingName(false);
                setIsEditingBio(false);
                setSelectedImageFile(null);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setErrorMessage('Failed to save profile. Please try again.');
        }
    };

    const resetImageChanges = () => {
        setProfileImage(profileData.image_url || DiaProfilePicture);
        setSelectedImageFile(null);
    };

    const handleInstagramClick = () => {
        trackEvent('instagram_profile_clicked');
        try {
            window.AndroidBridge.openDeeplink(`instagram://user?username=${profileData.social_media_handle.instagram}`);
        } catch (error) {
        }
    };

    const handleBack = () => {
        setShowProfile(false);
    };

    return (
        <div className="profile-container">
            <div className="profile-gradient-header">
                <button className="profile-nav-icon" onClick={handleBack} aria-label="Back">
                    <img src={backIcon} alt="Back" />
                </button>
                <div className="header-title-centered">Profile</div>
                <button className="profile-nav-icon" aria-label="Profile menu">
                    <img src={threeDotsIcon} alt="Menu" />
                </button>
            </div>
            <div className="profile-body">
                <div className="profile-top-card">
                    <div
                        className="profile-avatar-wrapper"
                        onClick={readOnly ? undefined : handleImageClick}
                        style={{ cursor: readOnly ? 'default' : 'pointer' }}
                    >
                        <img src={profileImage} alt="Profile" className="profile-avatar" />
                        {!readOnly && (
                            <>
                                <div className="profile-avatar-overlay">
                                    <span>{selectedImageFile ? 'Saving...' : 'Edit'}</span>
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />
                            </>
                        )}
                    </div>
                    <div className="profile-identity">
                        <div className="profile-name-row">
                            {isEditingName ? (
                                <div className="profile-name-edit">
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="profile-name-input"
                                        maxLength={50}
                                    />
                                    <img
                                        src={SaveIcon}
                                        alt="Save name"
                                        className="profile-save-icon"
                                        onClick={() => { trackEvent('profile_name_save_clicked'); saveProfile(); }}
                                    />
                                </div>
                            ) : (
                                <>
                                    <h1 className="profile-name-text">{name}</h1>
                                    {!readOnly && (
                                        <img
                                            src={editIcon}
                                            alt="Edit name"
                                            className="profile-edit-icon"
                                            onClick={() => setIsEditingName(true)}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                        <p className="profile-handle">{displayHandle}</p>
                        {roleLine && <p className="profile-role-line">{roleLine}</p>}
                        {qualificationValue && (
                            <p className="profile-secondary-line">{qualificationValue}</p>
                        )}
                    </div>
                </div>
                <div className="profile-metrics-grid">
                    {metrics.map((metric) => (
                        <div key={metric.id} className="profile-metric-card">
                            <div className="metric-label-row">
                                <span className="metric-value">{metric.value}</span>
                                <span className="metric-caption">{metric.label}</span>
                            </div>
                            <div className="metric-progress-track">
                                <div
                                    className="metric-progress-bar"
                                    style={{ width: `${metric.progress}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="profile-bio-card">
                    <div className="profile-bio-header">
                        <span>Bio</span>
                        {!readOnly && (
                            isEditingBio ? (
                                <img
                                    src={SaveIcon}
                                    alt="Save bio"
                                    className="profile-save-icon"
                                    onClick={() => { trackEvent('profile_bio_save_clicked'); saveProfile(); }}
                                />
                            ) : (
                                <img
                                    src={editIcon}
                                    alt="Edit bio"
                                    className="profile-edit-icon"
                                    onClick={() => setIsEditingBio(true)}
                                />
                            )
                        )}
                    </div>
                    <div className="profile-bio-content">
                        {isEditingBio ? (
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                className="profile-bio-textarea"
                                maxLength={250}
                            />
                        ) : (
                            <p>{bio || 'No Bio Added'}</p>
                        )}
                    </div>
                </div>
                {detailFields.length > 0 && (
                    <div className="profile-detail-card">
                        <div className="profile-detail-card-header">
                            <span>Details from onboarding</span>
                            <span className="detail-note">CA/CS specific</span>
                        </div>
                        <div className="profile-detail-grid">
                            {detailFields.map(field => (
                                <div key={field.label} className="profile-detail-row">
                                    <span className="profile-detail-label">{field.label}</span>
                                    <span className="profile-detail-value">{field.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {profileData?.social_media_handle?.instagram && (
                    <div
                        className="profile-social-row"
                        onClick={handleInstagramClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleInstagramClick();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <span>Follow @{profileData.social_media_handle.instagram} on Instagram</span>
                    </div>
                )}
                <div className="profile-tag-line">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Powered by <img src={chaimateLogo} alt="Chaimate Logo" style={{ marginLeft: '4px' }} />
                    </div>
                    <div>Made by Indians, for Indians.</div>
                </div>
                <Toast message={errorMessage} />
                <Loading isLoading={isLoading} />
            </div>
        </div>
    );
};

export default Profile;